import React from "react";

import { find, filter } from "lodash/fp";

import Divider from "@mui/material/Divider";

import { AccessSelectorDropdown } from "@app/common/access/ShareReport/styles";
import type { EntityAccessLevel } from "@app/common/types";

import type { DropdownProps } from "@rui/Dropdown/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";

import AccessSelectorItem from "./AccessSelectorItem";
import AccessLevelIcon from "../AccessLevelIcon";
import { ACCESS_LEVELS } from "../utils";

export interface AccessSelectorProps extends Omit<DropdownProps, "name"> {
  value: EntityAccessLevel;
  removable?: boolean;
  useIcons?: boolean;
}

const AccessSelector = ({
  value,
  removable = true,
  onChange,
  useIcons = false,
  ...dropdownProps
}: AccessSelectorProps) => {
  const selectedItem = getSelectedFromOptions<EntityAccessLevel>(
    ACCESS_LEVELS,
    value
  );

  const isDeprecatedSelectedItem = Boolean(
    find({ value: selectedItem?.value }, ACCESS_LEVELS)?.deprecated
  );

  // Show deprecated options if the selected item is deprecated, otherwise hide them
  const accessLevels = isDeprecatedSelectedItem
    ? ACCESS_LEVELS
    : filter<(typeof ACCESS_LEVELS)[number]>(
        (level) => !level.deprecated,
        ACCESS_LEVELS
      );

  return (
    <AccessSelectorDropdown
      name="Roles"
      selectedItem={selectedItem}
      onChange={onChange}
      wrapperSx={{ height: "100%", flexShrink: 0 }}
      renderSelectedItem={
        useIcons
          ? (selectedItem) => {
              return (
                <AccessLevelIcon
                  accessLevel={selectedItem.value as EntityAccessLevel}
                />
              );
            }
          : undefined
      }
      sx={{ minWidth: useIcons ? undefined : "87px" }}
      {...dropdownProps}
    >
      {accessLevels.map((r) => (
        <AccessSelectorItem key={r.value} {...r} selected={r.value === value} />
      ))}
      {removable && <Divider sx={{ mx: 2 }} />}
      {removable && (
        <AccessSelectorItem
          key="remove"
          value="remove"
          label="Remove Access"
          selected={false}
        />
      )}
    </AccessSelectorDropdown>
  );
};

export default AccessSelector;
